import { AdminMessage } from "@components/admin/admin-helpers"
import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { Button } from "@components/ui/Button"
import { formatDateTime } from "@utils/date-format"
import { defineBlock, EditableText } from "eddev/blocks"
import { Link } from "eddev/routing"
import { ReactNode } from "react"
import { ChildFilmTileFragment, MicroSessionTileFragment } from "../../types.graphql"

export const meta: BlockMeta = {
  title: "Event Hierarchy",
  icon: "format-aside",
  tags: ["film-text"],
  description:
    "Displays information about child and parent events. This is usually the case for short films, features with a short film, or a collection of shorts.",
  category: "Film",
  conditions: {
    multiple: false,
    postTypes: ["film"],
  },
}

export default defineBlock("film/film-hierarchy", (props) => {
  let contents: ReactNode = null
  const parentFilms = props.film?.parentFilms
  const childFilms = props.film?.childFilms

  if (parentFilms?.length) {
    contents = (
      <>
        {contents}
        <div className="my-6">
          <EditableText
            store="parentsLabel"
            as="h4"
            className="type-title-xs mb-3"
            defaultValue={`'${props.film?.title}' is screening with:`}
          />
          <div className="flex flex-col gap-2">
            {parentFilms.map((film) => (
              <MicroFilmTile key={film?.uri} {...film!} />
            ))}
          </div>
        </div>
      </>
    )
  }

  if (childFilms?.length) {
    contents = (
      <>
        {contents}
        <div className="my-6">
          <EditableText
            store="childrenLabel"
            as="h4"
            className="type-title-xs mb-3"
            defaultValue="Films showing at this event:"
          />
          <div className="flex flex-col gap-2">
            {childFilms.map((film) => (
              <MicroFilmTile key={film?.uri} {...film!} />
            ))}
          </div>
        </div>
      </>
    )
  }

  if (contents) {
    return <>{contents}</>
  } else if (env.admin) {
    return <AdminMessage>No parent or child events to display.</AdminMessage>
  }
})

function MicroSessionTile(props: MicroSessionTileFragment) {
  return (
    <div className="flex items-center gap-4">
      <ResponsiveImage {...props?.film?.media?.square} className="w-8 h-8 flex-none" />
      <div className="stack-y-2 flex-1">
        <h4 className="type-body">{props.film?.title}</h4>
        <p className="type-caption">
          {formatDateTime(props.info?.dateTime!)}, {props.info?.venue?.title}
        </p>
      </div>
      <div className="flex-none">
        <Link href={props.uri}>
          <Button>View</Button>
        </Link>
      </div>
    </div>
  )
}

function MicroFilmTile(props: ChildFilmTileFragment) {
  return (
    <div className="flex items-start gap-4 bg-grey8 p-3">
      <ResponsiveImage {...props?.media?.square} className="size-10 flex-none" />
      <div className="stack-y-1 flex-1">
        <h4 className="type-label-l">{props?.title}</h4>
        <div className="type-body-xs" dangerouslySetInnerHTML={{ __html: props.excerpt! }} />
        <Link href={props.uri} as={Button} rightIcon="arrowRight" size="sm">
          View
        </Link>
      </div>
    </div>
  )
}
